import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { EmojiCategory, RawEmoji } from './emoji.model';
import { detectMaxEmojiVersion } from './emoji-version';

/** Self-hosted emojibase dataset (works offline, no third-party CDN fetch). */
const DATA_URL = 'assets/emoji-picker-data.json';

/**
 * Maps emojibase `group` indices to the picker's display categories. Group 2
 * (skin-tone components) is intentionally omitted, and groups 0+1 (Smileys &
 * Emotion / People & Body) are merged into a single "Smiley & People" section,
 * matching the design.
 */
const CATEGORY_CONFIG: ReadonlyArray<{
  id: string;
  label: string;
  icon: string;
  groups: number[];
}> = [
  { id: 'people', label: 'Smiley & People', icon: '😀', groups: [0, 1] },
  { id: 'nature', label: 'Animals & Nature', icon: '🐻', groups: [3] },
  { id: 'food', label: 'Food & Drink', icon: '🍔', groups: [4] },
  { id: 'places', label: 'Travel & Places', icon: '✈️', groups: [5] },
  { id: 'activities', label: 'Activities', icon: '⚽', groups: [6] },
  { id: 'objects', label: 'Objects', icon: '💡', groups: [7] },
  { id: 'symbols', label: 'Symbols', icon: '🔣', groups: [8] },
  { id: 'flags', label: 'Flags', icon: '🏁', groups: [9] },
];

/**
 * Loads and indexes the emoji dataset. Provided in root so the (potentially
 * large) JSON is fetched and parsed once for the whole app, regardless of how
 * many times the picker is opened.
 */
@Injectable({ providedIn: 'root' })
export class EmojiDataService {
  private readonly http = inject(HttpClient);

  /** All emoji from the dataset (empty until the JSON resolves). */
  readonly all = toSignal(
    this.http.get<RawEmoji[]>(DATA_URL).pipe(map((list) => list ?? [])),
    { initialValue: [] as RawEmoji[] },
  );

  /**
   * Highest Emoji spec version this device's font can render, probed once. The
   * dataset renders native glyphs, so anything newer than this shows as a tofu
   * box (notably on older macOS) and is filtered out below. `Infinity` when
   * detection is unavailable — then nothing is filtered.
   */
  private readonly maxVersion = computed(() => detectMaxEmojiVersion());

  /**
   * Emoji the running platform can actually render, by spec version. Everything
   * downstream (categories, lookup, search) is built from this so unsupported
   * glyphs never reach the UI.
   */
  readonly supported = computed<RawEmoji[]>(() => {
    const max = this.maxVersion();
    if (max === Infinity) return this.all();
    return this.all().filter((e) => (e.version ?? 0) <= max);
  });

  /** Display categories, each sorted by the dataset's canonical `order`. */
  readonly categories = computed<EmojiCategory[]>(() => {
    const all = this.supported();
    if (!all.length) return [];
    return CATEGORY_CONFIG.map((cfg) => ({
      id: cfg.id,
      label: cfg.label,
      icon: cfg.icon,
      emojis: all
        .filter((e) => cfg.groups.includes(e.group))
        .sort((a, b) => a.order - b.order),
    })).filter((category) => category.emojis.length > 0);
  });

  /** Lookup from rendered glyph → record, for resolving persisted recents. */
  readonly byNative = computed<Map<string, RawEmoji>>(() => {
    const lookup = new Map<string, RawEmoji>();
    for (const emoji of this.supported()) lookup.set(emoji.emoji, emoji);
    return lookup;
  });

  /** Lowercased search haystack per emoji, rebuilt once per dataset load. */
  private readonly index = computed<{ emoji: RawEmoji; text: string }[]>(() =>
    this.supported().map((emoji) => ({
      emoji,
      text: [
        emoji.annotation,
        ...(emoji.shortcodes ?? []),
        ...(emoji.tags ?? []),
      ]
        .join(' ')
        .toLowerCase(),
    })),
  );

  /**
   * Starts the dataset fetch ahead of the picker opening (e.g. on chat open) so
   * it's warm by first use instead of downloading + parsing at click time. The
   * `toSignal` above already subscribes on construction, so simply injecting
   * this service triggers the load; this method just lets callers express that
   * intent explicitly (and reads `all` so it can't be tree-shaken away).
   */
  preload(): void {
    this.all();
  }

  /** AND substring match across annotation, shortcodes and tags. */
  search(query: string): RawEmoji[] {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return this.index()
      .filter(({ text }) => tokens.every((token) => text.includes(token)))
      .map(({ emoji }) => emoji);
  }
}
